import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, IconButton, Box, Typography, CircularProgress, Tooltip
} from '@mui/material';
import { Close, PhoneAndroid, Refresh, WifiOutlined, SignalCellularAlt, BatteryFull, Schedule } from '@mui/icons-material';
import { SectionServices } from './SectionServices';

/* ─── helpers ────────────────────────────────────────────── */
const AR_PAD = {
    '1:1': '100%', '4:3': '75%', '16:9': '56.25%',
    '3:2': '66.6%', '3:4': '133.3%', '9:16': '177.7%',
};

// Default card widths for horizontal_scroll by aspect ratio (portrait = thinner, landscape = wider)
const AR_DEFAULT_W = {
    '9:16': 72, '3:4': 80, '1:1': 90, '4:3': 110, '16:9': 140, '3:2': 105,
};

const FIXED_PREVIEW_WIDTH = 360;
const FIXED_PREVIEW_HEIGHT = 740;
const POPUP_PREVIEW_SCALE = 0.62;
const INLINE_PREVIEW_SCALE = 0.62;

const numberOption = (value, fallback = 0) => {
    if (value === '' || value === null || value === undefined) return fallback;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const getBgStyle = (section) => {
    const bgUrl = section.backgroundImage || section.desktopBackgroundImage;

    if (section.backgroundType === 'color' && section.backgroundColor)
        return { backgroundColor: section.backgroundColor };
    if ((section.backgroundType === 'image' || section.backgroundType === 'gif' || section.backgroundType === 'video' || bgUrl) && bgUrl) {
        const url = bgUrl.toLowerCase();
        const isVideo = url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.ogg') || url.endsWith('.mov') || section.backgroundType === 'video';
        if (isVideo) {
            return { backgroundColor: '#000', position: 'relative' };
        }
        return { backgroundColor: 'transparent' };
    }
    if (section.backgroundType === 'gradient') {
        const o = section.displayOptions || {};
        const start = o.gradientStartColor || '#667eea';
        const end = o.gradientEndColor || '#764ba2';
        return { background: `linear-gradient(135deg, ${start} 0%, ${end} 100%)` };
    }
    return { backgroundColor: '#fff' };
};

const posToAlign = (pos) => {
    if (pos === 'center') return 'center';
    if (pos === 'right') return 'right';
    return 'left';
};

const normalizeTitle = (value) => (value || '').toString().trim().toLowerCase();
const isBookAgainSection = (section) => normalizeTitle(section?.title) === 'book-again';
const isLiveActive = (record, now = new Date()) => {
    const start = record?.startDateTime ? new Date(record.startDateTime) : null;
    const end = record?.endDateTime ? new Date(record.endDateTime) : null;

    return record?.isActive !== false
        && (!start || start <= now)
        && (!end || end >= now);
};
const getSectionPageType = (section) => section?.displayOptions?.pageType || 'section';

/* ─── load Google Fonts for preview ─────────────────────── */
const GOOGLE_FONTS = ['Roboto','Open Sans','Lato','Montserrat','Poppins','Raleway','Nunito','Inter','Playfair Display'];
const loadGoogleFonts = () => {
    const id = 'preview-google-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${GOOGLE_FONTS.map(f => `family=${f.replace(/ /g,'+')}:wght@400;600;700`).join('&')}&display=swap`;
    document.head.appendChild(link);
};
loadGoogleFonts();

/* ─── badge ──────────────────────────────────────────────── */
const BadgeLabel = ({ text, color, textColor, position }) => {
    if (!text) return null;
    const posMap = {
        'top-left': { top: 4, left: 4 },
        'top-right': { top: 4, right: 4 },
        'bottom-left': { bottom: 4, left: 4 },
        'bottom-right': { bottom: 4, right: 4 },
    };
    return (
        <Box sx={{
            position: 'absolute', zIndex: 2, ...posMap[position || 'top-right'],
            backgroundColor: color || '#FF6B35',
            color: textColor || '#FFFFFF',
            borderRadius: '4px', px: '4px', py: '1px',
            fontSize: 8, fontWeight: 700, lineHeight: 1.4,
            whiteSpace: 'nowrap', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        }}>
            {text}
        </Box>
    );
};

/* ─── item image ─────────────────────────────────────────── */
const ItemImage = ({ item, sectionDisplayOpts, sectionType, hideBadge = false }) => {
    const itemAr = item.aspectRatio;
    const secAr = sectionDisplayOpts?.aspectRatio;
    const ar = (itemAr && itemAr !== 'auto') ? itemAr : ((secAr && secAr !== 'auto') ? secAr : null);
    const isCircle = secAr === '1:1';

    // Item JSON is the source of truth; section values are only fallbacks.
    const rawCardW = item.displayOptions?.cardWidth ?? sectionDisplayOpts?.cardWidth;
    const rawCardH = item.displayOptions?.cardHeight ?? sectionDisplayOpts?.cardHeight;
    const hasFixedW = rawCardW && rawCardW !== '' && parseInt(rawCardW) > 0;
    const hasFixedH = rawCardH && rawCardH !== '' && parseInt(rawCardH) > 0;

    // Determine width
    let width;
    if (sectionType === 'vertical_list') {
        width = hasFixedW ? parseInt(rawCardW) : 56;
    } else if (hasFixedW) {
        width = parseInt(rawCardW);
    } else if (ar && AR_DEFAULT_W[ar]) {
        width = AR_DEFAULT_W[ar];
    } else {
        width = '100%';
    }

    const imgUrl = item.imageUrl || item.thumbnailUrl || null;
    const imageFit = item.displayOptions?.imageFit || item.displayOptions?.objectFit || 'contain';

    // Use aspect-ratio padding trick if no fixed height set
    const useAspectPad = ar && AR_PAD[ar] && !hasFixedH;
    const fixedH = hasFixedH ? parseInt(rawCardH) : null;

    const containerSx = {
        position: 'relative',
        width: typeof width === 'number' ? width : width,
        flexShrink: 0,
        borderRadius: isCircle ? '50%' : '10px',
        overflow: 'hidden',
        backgroundColor: item.cardBackgroundColor || '',
        border: item.cardBorderColor ? `1.5px solid ${item.cardBorderColor}` : 'none',
        ...(useAspectPad
            ? { paddingBottom: AR_PAD[ar], height: 0 }
            : { height: fixedH || (sectionType === 'vertical_list' ? (typeof width === 'number' ? width : 56) : 80) }),
    };

    const innerSx = {
        position: useAspectPad ? 'absolute' : 'relative',
        top: 0, left: 0, width: '100%',
        height: useAspectPad ? '100%' : (fixedH || (sectionType === 'vertical_list' ? '100%' : 80)),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: item.cardBackgroundColor || '',
    };

    return (
        <Box sx={containerSx}>
            <Box sx={innerSx}>
                {imgUrl
                    ? <img src={imgUrl} alt={item.title || ''} style={{ width: '100%', height: '100%', objectFit: imageFit, display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    : <Typography sx={{ fontSize: 8, color: '#aaa' }}>No Image</Typography>
                }
            </Box>
            {!hideBadge && <BadgeLabel text={item.badgeText} color={item.badgeColor} textColor={item.displayOptions?.badgeTextColor} position={item.badgePosition} />}
        </Box>
    );
};

/* ─── single item card ───────────────────────────────────── */
const ItemCard = ({ item, sectionDisplayOpts, sectionType, hideBadge = false }) => {
    const dOpts = item.displayOptions || {};
    const titleAlign = posToAlign(dOpts.titlePosition);
    const subAlign = posToAlign(dOpts.subTitlePosition);
    const descAlign = posToAlign(dOpts.descriptionPosition);

    // Scale font size for the small preview (phone width ~250px vs real ~390px → ~64% scale)
    const SCALE = 0.55;
    const scaledSize = (val, fallback) => {
        const parsed = parseInt(val);
        return (val && !isNaN(parsed)) ? Math.max(8, Math.round(parsed * SCALE)) : fallback;
    };

    const titleSx = {
        fontWeight: dOpts.titleFontWeight || 600,
        fontStyle: dOpts.titleFontItalic ? 'italic' : 'normal',
        color: dOpts.titleColor || '#111',
        fontFamily: (dOpts.titleFontFamily && dOpts.titleFontFamily !== 'Default') ? `'${dOpts.titleFontFamily}', sans-serif` : 'inherit',
        fontSize: `${scaledSize(dOpts.titleFontSize, 10)}px`,
        textAlign: titleAlign,
        lineHeight: 1.3,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    };
    const subTitleSx = {
        fontWeight: dOpts.subTitleFontWeight || 400,
        fontStyle: dOpts.subTitleFontItalic ? 'italic' : 'normal',
        color: dOpts.subTitleColor || '#888',
        fontFamily: (dOpts.subTitleFontFamily && dOpts.subTitleFontFamily !== 'Default') ? `'${dOpts.subTitleFontFamily}', sans-serif` : 'inherit',
        fontSize: `${scaledSize(dOpts.subTitleFontSize, 9)}px`,
        textAlign: subAlign,
        lineHeight: 1.2,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    };
    const descSx = {
        fontWeight: dOpts.descriptionFontWeight || 400,
        fontStyle: dOpts.descriptionFontItalic ? 'italic' : 'normal',
        color: dOpts.descriptionColor || '#999',
        fontFamily: (dOpts.descriptionFontFamily && dOpts.descriptionFontFamily !== 'Default') ? `'${dOpts.descriptionFontFamily}', sans-serif` : 'inherit',
        fontSize: `${scaledSize(dOpts.descriptionFontSize, 8)}px`,
        textAlign: descAlign,
        lineHeight: 1.2,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    };

    // Scale margin/padding too
    const sp = (val, fallback = '2px') => {
        const parsed = parseInt(val);
        return (val && !isNaN(parsed) && parsed > 0) ? `${Math.round(parsed * SCALE)}px` : fallback;
    };
    const cardMargin = {
        mt: sp(dOpts.marginTop, 0),
        mr: sp(dOpts.marginRight, 0),
        mb: sp(dOpts.marginBottom, 0),
        ml: sp(dOpts.marginLeft, 0),
    };
    const cardPadding = {
        pt: sp(dOpts.paddingTop),
        pr: sp(dOpts.paddingRight),
        pb: sp(dOpts.paddingBottom),
        pl: sp(dOpts.paddingLeft),
    };

    if (sectionType === 'vertical_list') {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                backgroundColor: item.cardBackgroundColor || '#fff',
               
                border: item.cardBorderColor ? `1px solid ${item.cardBorderColor}` : '1px solid #f0f0f0',
              
                ...cardMargin,
            }}>
                <ItemImage item={item} sectionDisplayOpts={sectionDisplayOpts} sectionType={sectionType} hideBadge={hideBadge} />
                <Box sx={{ flex: 1, minWidth: 0, ...cardPadding }}>
                    {dOpts.showTitle !== false && item.title && (
                        <Typography sx={titleSx} noWrap>{item.title}</Typography>
                    )}
                    {dOpts.showSubTitle !== false && item.subtitle?.trim() && (
                        <Typography sx={subTitleSx} noWrap>{item.subtitle}</Typography>
                    )}
                    {dOpts.showDescription && item.description?.trim() && (
                        <Typography sx={descSx} noWrap>{item.description}</Typography>
                    )}
                    {hideBadge && item.lastBooked && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: `${parseInt(sectionDisplayOpts?.bookAgainTimeGap ?? 0, 10)}px`,
                            mt: 0.5,
                            color: '#888'
                        }}>
                            <Schedule sx={{ fontSize: 9, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 8, lineHeight: 1 }}>{item.lastBooked}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', ...cardMargin }}>
            <ItemImage item={item} sectionDisplayOpts={sectionDisplayOpts} sectionType={sectionType} hideBadge={hideBadge} />
            <Box sx={{ ...cardPadding }}>
                {dOpts.showTitle !== false && item.title && (
                    <Typography sx={titleSx}>{item.title}</Typography>
                )}
                {dOpts.showSubTitle !== false && item.subtitle?.trim() && (
                    <Typography sx={subTitleSx}>{item.subtitle}</Typography>
                )}
                {dOpts.showDescription && item.description?.trim() && (
                    <Typography sx={descSx}>{item.description}</Typography>
                )}
                {hideBadge && item.lastBooked && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: `${parseInt(sectionDisplayOpts?.bookAgainTimeGap ?? 0, 10)}px`,
                        mt: 0.5,
                        color: '#888'
                    }}>
                        <Schedule sx={{ fontSize: 9, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 8, lineHeight: 1 }}>{item.lastBooked}</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

/* ─── carousel (own component so hooks are top-level) ────── */
const CarouselLayout = ({
    visible,
    displayOpts,
    hideBadge = false,
    autoScrollDuration = null,
    showPagination = true
}) => {
    const [active, setActive] = useState(0);
    useEffect(() => {
        const durationSeconds = Number(autoScrollDuration);
        if (visible.length < 2 || !Number.isFinite(durationSeconds) || durationSeconds <= 0) return undefined;
        const timer = setInterval(() => setActive(p => (p + 1) % visible.length), durationSeconds * 1000);
        return () => clearInterval(timer);
    }, [autoScrollDuration, visible.length]);

    const cur = visible[active] || {};
    const itemCardHeight = cur.displayOptions?.cardHeight;
    const cardH = itemCardHeight && parseInt(itemCardHeight) > 0
        ? parseInt(itemCardHeight)
        : displayOpts?.cardHeight && parseInt(displayOpts.cardHeight) > 0
            ? parseInt(displayOpts.cardHeight)
            : 130;
    const imageFit = cur.displayOptions?.imageFit || cur.displayOptions?.objectFit || 'contain';

    return (
        <Box>
            <Box sx={{ position: 'relative', overflow: 'hidden', height: cardH, backgroundColor: cur.cardBackgroundColor || '' }}>
                {cur.imageUrl || cur.thumbnailUrl
                    ? <img src={cur.imageUrl || cur.thumbnailUrl} alt={cur.title || ''} style={{ width: '100%', height: '100%', objectFit: imageFit }} onError={(e) => { e.target.style.display = 'none'; }} />
                    : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: 10, color: '#aaa' }}>{cur.title || 'Slide'}</Typography></Box>
                }
                {!hideBadge && <BadgeLabel text={cur.badgeText} color={cur.badgeColor} textColor={cur.displayOptions?.badgeTextColor} position={cur.badgePosition} />}
                {cur.title && (
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.55))', px: 1.5, py: 1 }}>
                        <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{cur.title}</Typography>
                    </Box>
                )}
            </Box>
            {showPagination && visible.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0.75 }}>
                    {visible.map((_, i) => (
                        <Box key={i} onClick={() => setActive(i)} sx={{ cursor: 'pointer', width: i === active ? 16 : 6, height: 5, backgroundColor: i === active ? '#5856d6' : '#ccc', transition: 'all 0.3s' }} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

/* ─── items layout renderer ──────────────────────────────── */
const ItemsLayout = ({
    items,
    sectionType,
    displayOpts,
    maxItems,
    hideBadges = false,
    autoScrollDuration = null,
    showPagination = true
}) => {
    const visible = (items || []).filter(item => isLiveActive(item)).slice(0, maxItems || 10);

    if (!visible.length) {
        return null;
    }

    const secCardW = displayOpts?.cardWidth;
    const ar = displayOpts?.aspectRatio;
    const hasFixedW = secCardW && secCardW !== '' && parseInt(secCardW) > 0;

    // Resolve each card from item JSON first, then fall back to section JSON.
    const getHScrollMinW = (item) => {
        const itemCardW = item.displayOptions?.cardWidth;
        if (itemCardW && parseInt(itemCardW) > 0) return parseInt(itemCardW);
        if (hasFixedW) return parseInt(secCardW);
        // prefer item-level AR, then section AR
        const itemAr = item.aspectRatio && item.aspectRatio !== 'auto' ? item.aspectRatio : null;
        const resolvedAr = itemAr || (ar && ar !== 'auto' ? ar : null);
        return resolvedAr && AR_DEFAULT_W[resolvedAr] ? AR_DEFAULT_W[resolvedAr] : 90;
    };

    if (sectionType === 'horizontal_scroll') {
        return (
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.25, '&::-webkit-scrollbar': { height: 0 } }}>
                {visible.map(item => (
                    <Box key={item.id} sx={{ minWidth: getHScrollMinW(item), flexShrink: 0 }}>
                        <ItemCard item={item} sectionDisplayOpts={displayOpts} sectionType={sectionType} hideBadge={hideBadges} />
                    </Box>
                ))}
            </Box>
        );
    }

    if (sectionType === 'carousel') {
        return (
            <CarouselLayout
                visible={visible}
                displayOpts={displayOpts}
                hideBadge={hideBadges}
                autoScrollDuration={autoScrollDuration}
            />
        );
    }

    if (sectionType === 'banner') {
        return (
            <CarouselLayout
                visible={visible}
                displayOpts={displayOpts}
                hideBadge={hideBadges}
                autoScrollDuration={autoScrollDuration}
                showPagination={showPagination}
            />
        );
    }

    if (sectionType === 'fixed_banner') {
        const item = visible[0];
        const itemCardHeight = item?.displayOptions?.cardHeight;
        const bannerH = itemCardHeight && parseInt(itemCardHeight) > 0
            ? parseInt(itemCardHeight)
            : displayOpts?.cardHeight && parseInt(displayOpts.cardHeight) > 0
                ? parseInt(displayOpts.cardHeight)
                : 110;
        const imageFit = item?.displayOptions?.imageFit || item?.displayOptions?.objectFit || 'contain';
        return (
            <Box sx={{ position: 'relative', overflow: 'hidden', height: bannerH, backgroundColor: item?.cardBackgroundColor || '', border: item?.cardBorderColor ? `1.5px solid ${item.cardBorderColor}` : 'none' }}>
                {item?.imageUrl || item?.thumbnailUrl
                    ? <img src={item.imageUrl || item.thumbnailUrl} alt={item?.title || ''} style={{ width: '100%', height: '100%', objectFit: imageFit }} onError={(e) => { e.target.style.display = 'none'; }} />
                    : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: 10, color: '#aaa' }}>Banner</Typography></Box>
                }
                {!hideBadges && <BadgeLabel text={item?.badgeText} color={item?.badgeColor} textColor={item?.displayOptions?.badgeTextColor} position={item?.badgePosition} />}
                {item?.title && (
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', px: 1.5, py: 1 }}>
                        <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>{item.title}</Typography>
                        {item.subtitle?.trim() && <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 10 }}>{item.subtitle}</Typography>}
                    </Box>
                )}
            </Box>
        );
    }

    if (sectionType === 'vertical_list') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {visible.map(item => <ItemCard key={item.id} item={item} sectionDisplayOpts={displayOpts} sectionType={sectionType} hideBadge={hideBadges} />)}
            </Box>
        );
    }

    const colMap = { grid_2_column: 2, grid_3_column: 3, grid_4_column: 4 };
    if (colMap[sectionType]) {
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${colMap[sectionType]}, 1fr)`, gap: 1 }}>
                {visible.map(item => <ItemCard key={item.id} item={item} sectionDisplayOpts={displayOpts} sectionType={sectionType} hideBadge={hideBadges} />)}
            </Box>
        );
    }

    if (sectionType === 'mixed_grid') {
        const [first, ...rest] = visible;
        const firstImageFit = first?.displayOptions?.imageFit || first?.displayOptions?.objectFit || 'contain';
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 1 }}>
                <Box sx={{ gridRow: rest.length > 1 ? '1 / 3' : '1', position: 'relative', overflow: 'hidden', backgroundColor: first?.cardBackgroundColor || '', height: 160 }}>
                    {first?.imageUrl || first?.thumbnailUrl
                        ? <img src={first.imageUrl || first.thumbnailUrl} alt={first?.title || ''} style={{ width: '100%', height: '100%', objectFit: firstImageFit }} onError={(e) => { e.target.style.display = 'none'; }} />
                        : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: 10, color: '#aaa' }}>{first?.title}</Typography></Box>
                    }
                    {!hideBadges && <BadgeLabel text={first?.badgeText} color={first?.badgeColor} textColor={first?.displayOptions?.badgeTextColor} position={first?.badgePosition} />}
                </Box>
                {rest.slice(0, 2).map(item => (
                    <ItemCard key={item.id} item={item} sectionDisplayOpts={{ ...displayOpts, cardHeight: 76 }} sectionType={sectionType} hideBadge={hideBadges} />
                ))}
            </Box>
        );
    }

    return null;
};

/* ─── single section block ───────────────────────────────── */
const SectionBlock = ({ section, isCurrentSection }) => {
    const o = section.displayOptions || {};
    const sectionLayoutType = o.sectionType || section.sectionType;
    const isHeaderSection = getSectionPageType(section) === 'header';
    const itemsBottomOffset = numberOption(o.itemsBottomOffset, -20);
    const previewFontSize = (value, fallback) => {
        const parsed = parseInt(value, 10);
        return Number.isFinite(parsed) ? Math.max(7, Math.round(parsed * 0.65)) : fallback;
    };
    const customFontFaces = [
        ...(Array.isArray(o.customFonts) ? o.customFonts : []),
        ...(o.customFontName && o.customFontUrl ? [{ name: o.customFontName, url: o.customFontUrl }] : [])
    ].filter((font, index, fonts) => (
        font?.name && font?.url && fonts.findIndex(item => item?.name === font.name) === index
    )).map(font => {
        const family = font.name.replace(/[^a-zA-Z0-9 _-]/g, '').trim();
        const url = font.url.replace(/["'\\\n\r]/g, '');
        return family && url
            ? `@font-face { font-family: '${family}'; src: url('${url}'); font-style: normal; font-display: swap; }`
            : '';
    }).join('\n');

    const ptop = numberOption(o.sectionPadding?.top, 8);
    const pbot = numberOption(o.sectionPadding?.bottom, 8);
    const pleft = numberOption(o.sectionPadding?.left, 8);
    const pright = numberOption(o.sectionPadding?.right, 8);
    const mtop = numberOption(o.sectionMargin?.top);
    const mbot = numberOption(o.sectionMargin?.bottom);
    const mleft = numberOption(o.sectionMargin?.left);
    const mright = numberOption(o.sectionMargin?.right);
    const bgUrl = section.backgroundImage || section.desktopBackgroundImage;

    const isVideoBg = bgUrl && (
        bgUrl.toLowerCase().endsWith('.mp4') ||
        bgUrl.toLowerCase().endsWith('.webm') ||
        bgUrl.toLowerCase().endsWith('.ogg') ||
        bgUrl.toLowerCase().endsWith('.mov') ||
        section.backgroundType === 'video'
    );

    const hasSubtitleHighlightColor =
        o.subTitleBackgroundColor &&
        o.subTitleBackgroundColor !== 'none' &&
        o.subTitleBackgroundColor.toUpperCase() !== '#FFFFFF' &&
        o.subTitleBackgroundColor.toUpperCase() !== '#FFFFFFFF';
    const showSubtitleHighlight = o.enableSubTitleBackgroundColor !== undefined
        ? !!o.enableSubTitleBackgroundColor
        : hasSubtitleHighlightColor;
    const isImageBg = !!bgUrl && !isVideoBg;
    const hasCustomImageHeight = o.cardHeight && parseInt(o.cardHeight) > 0;
    const useNaturalImageHeight = isImageBg && !hasCustomImageHeight;

    return (
        <Box sx={{
            ...getBgStyle(section),
            pt: `${ptop}px`, pb: `${pbot}px`,
            pl: `${pleft}px`, pr: `${pright}px`,
            mt: `${mtop}px`, mb: `${mbot}px`,
            ml: `${mleft}px`, mr: `${mright}px`,
            boxShadow: section.backgroundType !== 'none' ? 'none' : 'none',
            minHeight: isImageBg && hasCustomImageHeight ? `${parseInt(o.cardHeight)}px` : undefined,
            overflow: 'hidden',
            position: 'relative',
            outline: isCurrentSection ? '2px solid #5856d6' : 'none',
            outlineOffset: '-2px',
        }}>
            {customFontFaces && <style>{customFontFaces}</style>}
            {isImageBg && (
                <Box
                    component="img"
                    src={bgUrl}
                    alt=""
                    aria-hidden="true"
                    sx={{
                        position: useNaturalImageHeight ? 'relative' : 'absolute',
                        inset: useNaturalImageHeight ? 'auto' : 0,
                        width: '100%',
                        height: useNaturalImageHeight ? 'auto' : '100%',
                        objectFit: 'contain',
                        display: 'block',
                        zIndex: 0,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {/* Background Video */}
            {isVideoBg && (
                <video
                    src={bgUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {/* Inner Content on top of background */}
            <Box sx={useNaturalImageHeight ? {
                position: 'absolute',
                top: `${ptop}px`,
                right: `${pright}px`,
                bottom: `${pbot}px`,
                left: `${pleft}px`,
                zIndex: 1,
                overflow: 'hidden'
            } : { position: 'relative', zIndex: 1 }}>
                {/* EDITING badge */}
                {isCurrentSection && (
                    <Box sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: '#5856d6', color: '#fff', fontSize: 7, fontWeight: 700, px: '5px', py: '2px', zIndex: 10, letterSpacing: 0.5 }}>
                        EDITING
                    </Box>
                )}
                {/* section header */}
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {o.showTitle !== false && section.title && (
                                <Typography sx={{
                                    fontSize: previewFontSize(o.titleFontSize, 13),
                                    fontWeight: o.titleFontWeight || 700,
                                    fontStyle: o.titleFontItalic ? 'italic' : 'normal',
                                    color: o.titleColor || '#111',
                                    fontFamily: o.titleFontFamily || 'inherit',
                                    textAlign: posToAlign(o.titleTextAlign || o.titlePosition),
                                    lineHeight: 1.3,
                                }}>
                                    {section.title}
                                </Typography>
                            )}
                            {o.showSubTitle !== false && section.subtitle?.trim() && (
                                <Typography component="span" sx={{
                                    display: 'inline-block',
                                    fontSize: previewFontSize(o.subTitleFontSize, 10),
                                    fontWeight: o.subTitleFontWeight || 400,
                                    fontStyle: o.subTitleFontItalic ? 'italic' : 'normal',
                                    mt: `${parseInt(o.headingSubtextSpacing ?? 2, 10)}px`,
                                    color: o.subTitleColor || '#555',
                                    fontFamily: o.subTitleFontFamily || 'inherit',
                                    textAlign: posToAlign(o.subTitleTextAlign || o.subTitlePosition),
                                    backgroundColor: showSubtitleHighlight ? o.subTitleBackgroundColor : 'transparent',
                                    
                                    px: showSubtitleHighlight ? '4px' : 0,
                                    lineHeight: 1.5,
                                }}>
                                    {section.subtitle}
                                </Typography>
                            )}
                            {o.showDescription && section.description?.trim() && (
                                <Typography sx={{
                                    fontSize: previewFontSize(o.descriptionFontSize, 9),
                                    fontWeight: o.descriptionFontWeight || 400,
                                    fontStyle: o.descriptionFontItalic ? 'italic' : 'normal',
                                    mt: `${parseInt(o.headingSubtextSpacing ?? 2, 10)}px`,
                                    color: o.descriptionColor || '#888',
                                    fontFamily: o.descriptionFontFamily || 'inherit',
                                    textAlign: posToAlign(o.descriptionTextAlign || o.descriptionPosition),
                                    lineHeight: 1.5,
                                }}>
                                    {section.description}
                                </Typography>
                            )}
                        </Box>
                        {section.showViewAll && (
                            <Typography sx={{
                                fontSize: previewFontSize(o.viewAllFontSize, 10),
                                color: '#5856d6',
                                fontWeight: o.viewAllFontWeight || 600,
                                flexShrink: 0,
                                mt: '2px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                            }}>
                                {section.viewAllText || 'View All'}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={isHeaderSection ? {
                    position: 'absolute',
                    right: 0,
                    bottom: `${itemsBottomOffset}px`,
                    left: 0
                } : undefined}>
                    <ItemsLayout
                        items={section.items || []}
                        sectionType={sectionLayoutType}
                        displayOpts={o}
                        maxItems={section.maxItemsToShow}
                        hideBadges={isBookAgainSection(section)}
                        autoScrollDuration={o.autoScrollDuration ?? section.autoScrollDuration}
                        showPagination={o.showPagination !== false}
                    />
                </Box>
            </Box>
        </Box>
    );
};

/* ─── full screen content ────────────────────────────────── */
const ScreenContent = ({ sections, activeSectionId, loading }) => {
    const headerSections = sections.filter(section => getSectionPageType(section) === 'header');
    const footerSections = sections.filter(section => getSectionPageType(section) === 'footer');
    const contentSections = sections.filter(section => !['header', 'footer'].includes(getSectionPageType(section)));
    const hasSections = sections.length > 0;
    const orderedSections = [...headerSections, ...contentSections, ...footerSections];

    return (
        <Box sx={{ backgroundColor: 'transparent', minHeight: '100%' }}>
            {loading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={22} sx={{ color: '#5856d6' }} />
                </Box>
            ) : !hasSections ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#bbb' }}>No sections found</Typography>
                </Box>
            ) : (
                orderedSections.map(section => (
                    <Box key={section.id} sx={{ mb: getSectionPageType(section) === 'footer' ? 0 : 1 }}>
                        <SectionBlock section={section} isCurrentSection={section.id === activeSectionId} />
                    </Box>
                ))
            )}
        </Box>
    );
};

/* ─── phone frame ────────────────────────────────────────── */
export const PhoneFrame = ({ sections, activeSectionId, loading, scale = 1 }) => {
    const width = FIXED_PREVIEW_WIDTH;
    const screenHeight = FIXED_PREVIEW_HEIGHT;
    const frameOuterHeight = screenHeight + 88;
    const frameOuterWidth = width + 48;

    return (
        <Box sx={{
            width: `${Math.ceil(frameOuterWidth * scale)}px`,
            height: `${Math.ceil(frameOuterHeight * scale)}px`,
            mx: 'auto',
            position: 'relative',
            userSelect: 'none'
        }}>
            <Box sx={{
                width: `${frameOuterWidth}px`,
                transform: scale === 1 ? 'none' : `scale(${scale})`,
                transformOrigin: 'top left',
                position: 'relative'
            }}>
                <Box sx={{
                    width: `${width}px`,
                    mx: 'auto',
                    border: '10px solid #1c1c1e', borderRadius: '44px',
                    overflow: 'hidden', background: 'white',
                    boxShadow: '0 0 0 2px #3a3a3c, 0 30px 80px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.08)',
                    position: 'relative',
                }}>
                    {/* Dynamic island */}
                    <Box sx={{  height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: 90, height: 24, backgroundColor: '#000', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.2 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                            <Box sx={{ width: 28, height: 5, backgroundColor: '#1a1a1a', borderRadius: 3 }} />
                        </Box>
                    </Box>
                    {/* Status bar */}
                    <Box sx={{ backgroundColor: '#fff', px: 1.5, py: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: -0.3 }}>9:41</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <SignalCellularAlt sx={{ fontSize: 11, color: '#111' }} />
                            <WifiOutlined sx={{ fontSize: 11, color: '#111' }} />
                            <BatteryFull sx={{ fontSize: 12, color: '#111' }} />
                        </Box>
                    </Box>
                    {/* Screen */}
                    <Box sx={{ height: screenHeight, overflowY: 'auto', backgroundColor: 'transparent', '&::-webkit-scrollbar': { width: 0 } }}>
                        <ScreenContent sections={sections} activeSectionId={activeSectionId} loading={loading} />
                    </Box>
                    {/* Home bar */}
                    <Box sx={{ backgroundColor: '#fff', height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: 80, height: 4, backgroundColor: '#111', borderRadius: 2, opacity: 0.8 }} />
                    </Box>
                </Box>
                {/* Side buttons */}
                <Box sx={{ position: 'absolute', left: 10, top: 90, width: 4, height: 30, backgroundColor: '#333', borderRadius: '2px 0 0 2px' }} />
                <Box sx={{ position: 'absolute', left: 10, top: 130, width: 4, height: 44, backgroundColor: '#333', borderRadius: '2px 0 0 2px' }} />
                <Box sx={{ position: 'absolute', left: 10, top: 184, width: 4, height: 44, backgroundColor: '#333', borderRadius: '2px 0 0 2px' }} />
                <Box sx={{ position: 'absolute', right: 10, top: 130, width: 4, height: 64, backgroundColor: '#333', borderRadius: '0 2px 2px 0' }} />
            </Box>
        </Box>
    );
};

/* ─── main export ────────────────────────────────────────── */
const buildDraftSection = (formData, sectionId) => {
    if (!formData) return null;
    return {
        id: parseInt(sectionId) > 0 ? parseInt(sectionId) : 'draft-section-preview',
        title: formData.title || 'New Section',
        subtitle: formData.subtitle || '',
        description: formData.description || '',
        sectionType: formData.sectionType || 'horizontal_scroll',
        backgroundType: formData.backgroundType || 'none',
        backgroundColor: formData.backgroundColor || '#FFFFFF',
        backgroundImage: formData.backgroundImage || null,
        showViewAll: formData.showViewAll || false,
        viewAllText: formData.viewAllText || 'View All',
        maxItemsToShow: formData.maxItemsToShow || 10,
        displayOptions: formData.displayOptions || {},
        startDateTime: formData.startDateTime || null,
        endDateTime: formData.endDateTime || null,
        isActive: formData.isActive ?? true,
        items: []
    };
};

const mergeAndFocusPreviewSections = (sections, formData, sectionId) => {
    const activeId = parseInt(sectionId) > 0 ? parseInt(sectionId) : 'draft-section-preview';
    let found = false;
    const merged = sections.map(s => {
        if (s.id === activeId && formData) {
            found = true;
            return {
                ...s,
                title: formData.title ?? s.title,
                subtitle: formData.subtitle ?? s.subtitle,
                description: formData.description ?? s.description,
                sectionType: formData.sectionType ?? s.sectionType,
                backgroundType: formData.backgroundType ?? s.backgroundType,
                backgroundColor: formData.backgroundColor ?? s.backgroundColor,
                backgroundImage: formData.backgroundImage ?? s.backgroundImage,
                showViewAll: formData.showViewAll ?? s.showViewAll,
                viewAllText: formData.viewAllText ?? s.viewAllText,
                maxItemsToShow: formData.maxItemsToShow ?? s.maxItemsToShow,
                displayOptions: formData.displayOptions ?? s.displayOptions,
                startDateTime: formData.startDateTime ?? s.startDateTime,
                endDateTime: formData.endDateTime ?? s.endDateTime,
                isActive: formData.isActive ?? s.isActive,
                items: s.items,
            };
        }
        return s;
    });
    if (!found && formData) {
        const draft = buildDraftSection(formData, sectionId);
        if (draft) merged.unshift(draft);
    }
    const liveSections = merged.filter(section => isLiveActive(section));
    const currentIndex = liveSections.findIndex(s => s.id === activeId);
    if (currentIndex > 0) {
        const [current] = liveSections.splice(currentIndex, 1);
        liveSections.unshift(current);
    }
    return { activeId, mergedSections: liveSections };
};

const fetchPreviewSectionData = async (sectionId) => {
    const previewResponse = await SectionServices.homeScreenPreview();
    let previewSections = Number(previewResponse.success) === 1
        ? SectionServices.normalizeHomeScreenSections(previewResponse.data)
        : [];

    const numericSectionId = parseInt(sectionId, 10);
    if (!Number.isFinite(numericSectionId) || numericSectionId <= 0) {
        return previewSections;
    }

    try {
        const detailsResponse = await SectionServices.getSectionById(numericSectionId);
        const [detailedSection] = Number(detailsResponse.success) === 1
            ? SectionServices.normalizeHomeScreenSections(detailsResponse.data)
            : [];

        if (!detailedSection) return previewSections;

        if (previewSections.some(section => section.id === numericSectionId)) {
            previewSections = previewSections.map(section => (
                section.id === numericSectionId ? { ...section, ...detailedSection } : section
            ));
        } else {
            previewSections = [detailedSection, ...previewSections];
        }
    } catch (error) {
        // Keep the grouped preview usable if the details request fails.
        console.error(error);
    }

    return previewSections;
};

const MobilePreview = ({ open, onClose, formData, sectionId }) => {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchSections = useCallback(async () => {
        setLoading(true);
        try {
            setSections(await fetchPreviewSectionData(sectionId));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [sectionId]);

    useEffect(() => {
        if (open) fetchSections();
    }, [fetchSections, open]);

    const { activeId, mergedSections } = mergeAndFocusPreviewSections(sections, formData, sectionId);

    const liveCount = sections.filter(section => isLiveActive(section)).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ sx: { borderRadius: 4, background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', width: { xs: 'calc(100vw - 24px)', sm: Math.ceil((FIXED_PREVIEW_WIDTH + 48) * POPUP_PREVIEW_SCALE) + 120 }, maxWidth: 'calc(100vw - 24px)', overflow: 'visible' } }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <PhoneAndroid sx={{ color: '#a78bfa', fontSize: 18 }} />
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.3 }}>
                        App Preview
                    </Typography>
                    <Box sx={{ backgroundColor: 'rgba(167,139,250,0.2)', px: 1, py: '2px', borderRadius: '20px', border: '1px solid rgba(167,139,250,0.4)' }}>
                        <Typography sx={{ fontSize: 9, color: '#a78bfa', fontWeight: 600, letterSpacing: 0.5 }}>
                            {liveCount} active sections
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchSections} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                        <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>
            {/* Phone */}
            <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 1.5, pt: 1, display: 'flex', justifyContent: 'center', overflow: 'visible' }}>
                <PhoneFrame
                    sections={mergedSections}
                    activeSectionId={activeId}
                    loading={loading}
                    scale={POPUP_PREVIEW_SCALE}
                />
            </Box>
            {/* Footer legend */}
            <Box sx={{ textAlign: 'center', pb: 2, px: 2 }}>
                <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: 0.3 }}>
                    {loading ? 'Loading...' : 'Fixed 360×740 preview · all active sections and items'}
                </Typography>
            </Box>
        </Dialog>
    );
};

export default MobilePreview;

/* ─── inline (always-visible) export ────────────────────── */
export const InlineSectionPreview = ({ formData, sectionId }) => {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchSections = useCallback(async () => {
        setLoading(true);
        try {
            setSections(await fetchPreviewSectionData(sectionId));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [sectionId]);

    useEffect(() => { fetchSections(); }, [fetchSections]);

    const { activeId, mergedSections } = mergeAndFocusPreviewSections(sections, formData, sectionId);
    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneAndroid sx={{ color: '#5856d6', fontSize: 16 }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#5856d6' }}>
                        Live Preview
                    </Typography>
                </Box>
                <Tooltip title="Refresh sections">
                    <IconButton size="small" onClick={fetchSections} sx={{ color: '#999' }}>
                        <Refresh sx={{ fontSize: 15 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <PhoneFrame
                sections={mergedSections}
                activeSectionId={activeId}
                loading={loading}
                scale={INLINE_PREVIEW_SCALE}
            />
            <Typography sx={{ fontSize: '0.6rem', color: '#bbb', textAlign: 'center', mt: 1 }}>
                Fixed 360×740 · Purple outline = currently editing
            </Typography>
        </Box>
    );
};
